import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useMemo, useState } from "react";
import { CalendarBlankIcon, MountainsIcon, ArrowClockwiseIcon, MapTrifoldIcon, ListBulletsIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import Discover from "@/pages/Discover";
import { DiscoverEventsProvider, useDiscoverEvents } from "@/context/DiscoverEventsContext";
import { getUpcomingRideEvents } from "@/lib/eventsApi";
import { EventCard } from "@/components/EventCard";
import { EventDetails } from "@/components/EventDetails";
import { trackEvent } from "@/lib/analytics";

const rad = Math.PI / 180;
function withinRadius(item, area) {
  if (!Number.isFinite(item.latitude) || !Number.isFinite(item.longitude)) return false;
  if (!area?.origin || !area.radiusKm) return true;
  const a = area.origin.lat * rad;
  const b = item.latitude * rad;
  const dLat = b - a;
  const dLng = (item.longitude - area.origin.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a) * Math.cos(b) * Math.sin(dLng / 2) ** 2;
  return 12742 * Math.asin(Math.min(1, Math.sqrt(h))) <= area.radiusKm;
}

function EventsControls() {
  const layer = useDiscoverEvents();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [mobileEventView, setMobileEventView] = useState("list");
  const [eventQuery, setEventQuery] = useState("");

  useEffect(() => {
    if (!layer.visible) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    getUpcomingRideEvents({ days: 90, limit: 200, signal: controller.signal })
      .then((items) => { if (!controller.signal.aborted) layer.setEvents(items); })
      .catch((err) => { if (!controller.signal.aborted) { layer.setEvents([]); setError(err.message || translateLabel("Events are unavailable")); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  // Fetch only on entering the event layer or explicitly refreshing, not on every selection.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.visible, retry]);

  const categories = useMemo(() => [...new Set(layer.events.map((event) => event.category))].sort(), [layer.events]);
  const visibleEvents = useMemo(() => layer.events.filter((event) =>
    (layer.category === "all" || event.category === layer.category) && withinRadius(event, layer.searchArea) &&
    [event.title, event.city, event.venueName, event.category].some((value) =>
      String(value || "").toLocaleLowerCase().includes(eventQuery.trim().toLocaleLowerCase()))
  ), [layer.events, layer.category, layer.searchArea, eventQuery]);
  // Never show details for a pin filtered out of the active Discovery area/category.
  const selectedEvent = visibleEvents.find((event) => event.id === layer.selectedEventId) || null;
  const selectEvent = (event) => {
    trackEvent("event_view", {
      event_id: String(event.id),
      event_name: event.title || "",
      category: event.category || "",
      city: event.city || "",
    });
    layer.selectEvent(event);
    setMobileEventView("map");
  };
  useEffect(() => {
    if (!layer.visible) setMobileEventView("list");
  }, [layer.visible]);
  const changeVisibility = (value) => {
    layer.setVisible(value);
    layer.setSelectedEventId(null);
    setMobileEventView("list");
  };

  return <>
    <div className="absolute right-4 top-[4.5rem] z-[1300] hidden gap-1 rounded-full md:flex border border-white/15 bg-black/85 p-1 text-xs font-bold text-white shadow-xl backdrop-blur-xl md:top-20" role="group" aria-label={translateLabel("Discovery layer")}>
      <button type="button" data-testid="discover-spots-layer" aria-pressed={!layer.visible} onClick={() => changeVisibility(false)} className={`flex items-center gap-1 rounded-full px-3 py-2 ${!layer.visible ? "bg-[#CCFF00] text-black" : "text-zinc-300 hover:text-white"}`}><MountainsIcon size={15}/> {translateLabel("Spots")}</button>
      <button type="button" data-testid="discover-events-layer" aria-pressed={layer.visible} onClick={() => changeVisibility(true)} className={`flex items-center gap-1 rounded-full px-3 py-2 ${layer.visible ? "bg-violet-400 text-black" : "text-zinc-300 hover:text-white"}`}><CalendarBlankIcon size={15}/> {translateLabel("Events")}</button>
    </div>

    {layer.visible && <aside aria-label={translateLabel("Upcoming rides and events")} data-testid="discover-events-sidebar" className={`absolute bottom-0 left-0 top-16 z-[1150] w-full p-0 md:top-20 md:w-[27rem] md:p-3 ${mobileEventView === "map" ? "hidden md:block" : "block"}`}>
      <div className="flex h-full flex-col overflow-hidden border border-white/10 bg-black/90 text-white backdrop-blur-2xl md:rounded-3xl">
        <div className="space-y-3 border-b border-white/10 p-3 md:p-4">
          <label className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2.5">
            <MagnifyingGlassIcon size={18} className="shrink-0 text-zinc-400" />
            <input value={eventQuery} onChange={(event) => setEventQuery(event.target.value)}
              placeholder={translateLabel("Search events or places…")} aria-label={translateLabel("Search events or places")}
              className="min-w-0 flex-1 bg-transparent text-sm font-medium text-white placeholder:text-zinc-500 focus:outline-none" />
          </label>
          <div className="flex items-center justify-between"><h1 className="flex items-center gap-2 text-lg font-bold"><CalendarBlankIcon className="text-violet-400"/> {translateLabel("Events & Rides")}</h1><button type="button" aria-label={translateLabel("Refresh events")} onClick={() => setRetry((n) => n + 1)} className="rounded-lg p-2 hover:bg-white/10"><ArrowClockwiseIcon size={18}/></button></div>
          <p className="text-xs text-zinc-400">{translateLabel(layer.searchArea?.origin ? "Upcoming events within your Discovery radius." : "Upcoming events across all locations.")}</p>
          <label htmlFor="discover-event-category" className="block text-xs text-zinc-300">{translateLabel("Event type")}</label>
          <select id="discover-event-category" value={layer.category} onChange={(event) => { layer.setCategory(event.target.value); layer.setSelectedEventId(null); }} className="w-full rounded-lg border border-white/20 bg-[#202124] p-2 text-sm text-white"><option value="all">{translateLabel("All events")}</option>{categories.map((category) => <option key={category} value={category}>{category}</option>)}</select>
          <p aria-live="polite" className="text-xs text-zinc-400">{loading ? translateLabel("Loading events…") : translateLabel(visibleEvents.length === 1 ? "{count} upcoming event" : "{count} upcoming events", { count: visibleEvents.length })}</p>
        </div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
          {error && <p role="alert" className="rounded-lg border border-red-500/40 p-3 text-sm text-red-300">{error} <button type="button" className="underline" onClick={() => setRetry((n) => n + 1)}>{translateLabel("Retry")}</button></p>}
          {!loading && !error && !visibleEvents.length && <p className="p-3 text-sm text-zinc-400">{translateLabel("No upcoming events for this search. Try a wider radius or another category.")}</p>}
          {visibleEvents.map((event) => <EventCard key={event.id} event={event} selected={layer.selectedEventId === event.id} onSelect={selectEvent}/>)}
        </div>
      </div>
    </aside>}

    {layer.visible && selectedEvent && <div className="absolute bottom-0 right-0 z-[1400] h-[min(78dvh,44rem)] w-full overflow-hidden rounded-t-2xl border border-white/10 bg-[#111214] text-white shadow-2xl md:top-20 md:h-auto md:w-[25rem] md:rounded-none md:border-l"><EventDetails event={selectedEvent} onClose={() => layer.setSelectedEventId(null)} onSelectEvent={selectEvent}/></div>}
    {layer.visible && !selectedEvent && <button type="button" data-testid="event-mobile-view-toggle" className="absolute bottom-5 left-1/2 z-[1350] flex -translate-x-1/2 items-center gap-2 rounded-full bg-violet-400 px-5 py-3 text-sm font-bold text-black shadow-xl md:hidden" onClick={() => setMobileEventView((view) => view === "map" ? "list" : "map")}>{mobileEventView === "map" ? <><ListBulletsIcon size={18}/> {translateLabel("Events list")}</> : <><MapTrifoldIcon size={18}/> {translateLabel("Map")}</>}</button>}
  </>;
}

function DiscoverWithEvents() {
  const layer = useDiscoverEvents();
  return <div className={`relative h-[calc(100dvh-var(--app-footer-height))] w-full ${layer.visible ? "events-layer-active" : ""}`}>
    <Discover />
    <EventsControls />
    {/* Discover's own mobile list/map controls must not cover the Events map. */}
    <style>{`@media (max-width: 767px) {
      .events-layer-active > div:first-child > div.absolute.bottom-0.left-0.top-0 { display: none; }
      .events-layer-active [data-testid="mobile-view-toggle"] { display: none; }
    }`}</style>
  </div>;
}

export default function DiscoverEventsExperience() {
  return <DiscoverEventsProvider><DiscoverWithEvents /></DiscoverEventsProvider>;
}
